import { CUSTOMER_FORM_DIRTY_ATTR } from '../../../../app/pwa/customerFormDirtyGate.js';
import { toCustomerFacingErrorMessage } from '../../../../shared/errors/toCustomerFacingErrorMessage.js';
import { Card } from '../../../../shared/ui/Card.js';
import { Button } from '../../../../shared/ui/Button.js';
import { FormField } from '../../../../shared/ui/FormField.js';
import { CheckoutGuestSignInPrompt } from '../../../checkout/CheckoutGuestSignInPrompt.js';
import { CheckoutCurrencyPicker } from '../../../checkout/components/CheckoutCurrencyPicker.js';
import type { UsePhoneFirstDonationJourneyResult } from '../../hooks/usePhoneFirstDonationJourney.js';
import { resolveEmptyPaymentMethodsMessageKey } from '../phone-first-checkout/resolveEmptyPaymentMethodsMessageKey.js';

export type PhoneFirstDonationMethodStepViewProps = Pick<
  UsePhoneFirstDonationJourneyResult,
  | 't'
  | 'tenantCode'
  | 'authSession'
  | 'email'
  | 'setEmail'
  | 'emailError'
  | 'selectedMethod'
  | 'wantsTaxReceipt'
  | 'setWantsTaxReceipt'
  | 'wantsRecurring'
  | 'setWantsRecurring'
  | 'createSession'
  | 'startQr'
  | 'startGateway'
  | 'submitMethod'
  | 'errorMessage'
  | 'mobileQrAllowed'
  | 'mobileGatewayAllowed'
  | 'gatewayRailCandidate'
  | 'gatewayMethodsProbePending'
  | 'gatewayMethodsPayable'
  | 'showCheckoutCurrencyPicker'
  | 'checkoutCurrencyOptions'
  | 'selectedCheckoutCurrency'
  | 'currencyLocked'
  | 'handleCheckoutCurrencySelect'
>;

export function PhoneFirstDonationMethodStepView(
  props: PhoneFirstDonationMethodStepViewProps,
): JSX.Element {
  const {
    t,
    tenantCode,
    authSession,
    email,
    setEmail,
    emailError,
    selectedMethod,
    wantsTaxReceipt,
    setWantsTaxReceipt,
    wantsRecurring,
    setWantsRecurring,
    createSession,
    startQr,
    startGateway,
    submitMethod,
    errorMessage,
    mobileQrAllowed,
    mobileGatewayAllowed,
    gatewayRailCandidate,
    gatewayMethodsProbePending,
    gatewayMethodsPayable,
    showCheckoutCurrencyPicker,
    checkoutCurrencyOptions,
    selectedCheckoutCurrency,
    currencyLocked,
    handleCheckoutCurrencySelect,
  } = props;

  const safeErrorMessage =
    errorMessage != null
      ? toCustomerFacingErrorMessage(errorMessage, {
          fallback: t('donation.paymentStartFailed'),
        })
      : null;

  const inFlight = createSession.isPending || startQr.isPending || startGateway.isPending;
  const isDraftDirty =
    email.trim().length > 0 || wantsTaxReceipt || wantsRecurring;

  // Mirror product: full-rail loading only while probing and nothing else is offerable.
  const showGatewayProbeLoading = gatewayMethodsProbePending && !mobileQrAllowed;
  const showQrCta = !showGatewayProbeLoading && mobileQrAllowed;
  const showCardCta = !showGatewayProbeLoading && mobileGatewayAllowed;
  const showEmptyMethods =
    !showGatewayProbeLoading && !showQrCta && !showCardCta;
  const emptyMethodsMessageKey = showEmptyMethods
    ? resolveEmptyPaymentMethodsMessageKey({
        gatewayMethodsPayable,
        mobileQrAllowed,
        gatewayRailCandidate,
      })
    : null;

  return (
    <Card padded>
      <div
        className="flex flex-col gap-3"
        {...{ [CUSTOMER_FORM_DIRTY_ATTR]: isDraftDirty ? 'true' : 'false' }}
      >
        {authSession === null ? (
          <div>
            <FormField
              label={t('checkout.emailLabel')}
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder={t('checkout.emailPlaceholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              // G12 (b) — single-field guest email: errorText-alone (no FormErrorSummary).
              errorText={authSession === null ? (emailError ?? undefined) : undefined}
              helperText={t('donation.emailHelper')}
            />
            <CheckoutGuestSignInPrompt tenantCode={tenantCode} source="donate" />
          </div>
        ) : null}
        {showCheckoutCurrencyPicker && selectedCheckoutCurrency !== null ? (
          <CheckoutCurrencyPicker
            currencies={checkoutCurrencyOptions}
            selected={selectedCheckoutCurrency}
            locked={currencyLocked}
            pending={inFlight}
            onSelect={handleCheckoutCurrencySelect}
          />
        ) : null}
        <div className="flex flex-col gap-3 rounded-md border border-[var(--color-border)] p-3">
          <div className="flex items-start gap-2">
            <input
              id="donation-tax-receipt"
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--color-primary)]"
              checked={wantsTaxReceipt}
              onChange={(event) => {
                setWantsTaxReceipt(event.target.checked);
              }}
            />
            <div>
              <label
                htmlFor="donation-tax-receipt"
                className="text-sm font-medium text-[var(--color-on-surface)]"
              >
                {t('donation.taxReceiptLabel')}
              </label>
              <p className="mt-0.5 text-xs text-[var(--color-on-surface-muted)]">
                {t('donation.taxReceiptHelper')}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <input
              id="donation-recurring"
              type="checkbox"
              className="mt-1 h-4 w-4 shrink-0 accent-[var(--color-primary)]"
              checked={wantsRecurring}
              onChange={(event) => {
                setWantsRecurring(event.target.checked);
              }}
            />
            <div>
              <label
                htmlFor="donation-recurring"
                className="text-sm font-medium text-[var(--color-on-surface)]"
              >
                {t('donation.recurringLabel')}
              </label>
              <p className="mt-0.5 text-xs text-[var(--color-on-surface-muted)]">
                {t('donation.recurringHelper')}
              </p>
            </div>
          </div>
        </div>
        {showGatewayProbeLoading ? (
          <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
            {t('payment.gatewayMethods.loading')}
          </p>
        ) : null}
        {showQrCta ? (
          <Button
            intent={selectedMethod === 'qr' ? 'primary' : 'secondary'}
            size="lg"
            block
            disabled={inFlight}
            onClick={() => void submitMethod('qr')}
          >
            {t('donation.payWithQr')}
          </Button>
        ) : null}
        {showCardCta ? (
          <Button
            intent={selectedMethod === 'gateway' ? 'primary' : 'secondary'}
            size="lg"
            block
            disabled={inFlight}
            onClick={() => void submitMethod('gateway')}
          >
            {t('donation.payWithCard')}
          </Button>
        ) : null}
        {emptyMethodsMessageKey !== null ? (
          <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
            {t(emptyMethodsMessageKey)}
          </p>
        ) : null}
        {safeErrorMessage !== null ? (
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {safeErrorMessage}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
